<template>
  <div>
    <HomeScreenSectionLayout :sections="orderedEnabledHomeSections">
      <template #quickAccess>
        <div class="flex float-start flex-row lg:mx-auto lg:flex-col">
          <QuickAccessLinksComponent
            v-for="l in links"
            :key="l?._id"
            :url="l?.link"
            :icon="l?.icon"
          />
        </div>
      </template>

      <template #levelDashboard>
        <div
          v-if="weekGoals.length > 0"
          class="home-section home-section--overview overflow-y-visible w-full"
        >
          <header class="mb-4">
            <h1 class="text-3xl lg:text-4xl font-bold text-[var(--color-black)] m-0">
              {{ sectionHeading('levelDashboard', 'HomeComponent.homeOverviewWelcome') }}
            </h1>
            <p class="mt-2 text-base m-0 text-[var(--color-inactive-dark)]">
              {{ $t('HomeComponent.homeOverviewSubtitle', { date: homeOverviewUpdatedDate }) }}
            </p>
          </header>
        </div>

      
        <div class="home-section home-section--level-dashboard">
          <LevelDashboardSection />
        </div>
      </template>

      <template #levelGoals>
        <div class="home-section home-section--level-goals overflow-y-visible w-full">
          <div
            v-if="isInactive"
            class="bg-amber-50 border border-amber-300 rounded-lg p-4 my-4 flex items-start gap-3"
          >
            <i
              class="pi pi-exclamation-triangle text-amber-600 text-xl shrink-0"
              aria-hidden="true"
            />
            <span class="text-amber-800">
              {{
                $t('HomeComponent.noProgressForDays', { days: daysSinceLastProgress })
                  || `No progress for ${daysSinceLastProgress} days — your development is currently on hold.`
              }}
            </span>
          </div>
          <h1 class="text-3xl lg:text-4xl mt-4">
            {{ sectionHeading('levelGoals', 'HomeComponent.levelGoals') }}
          </h1>
          <div
            id="div_weekgoals"
            class="grid grid-cols-1 justify-start items-center my-5
              gap-x-4 md:grid-cols-2 md:gap-x-8 lg:mr-12 3xl:grid-cols-4"
          >
            <WeekgoalComponent
              v-for="goal in weekGoals"
              :key="goal"
              :title="goal?.title || { en: goal?.title?.en, de: goal?.title?.de } || ''"
              :id="goal.id"
              :img="goal.img"
              :xp="goal.xp"
              :assessmentType="goal.assessmentType"
              :type="goal.type"
              :completed="goal.completed"
              :progress="goal.progress"
              :requiredCompletions="goal.requiredCompletions"
            />
          </div>
        </div>
      </template>

      <template #teamspace>
        <div class="home-section home-section--teamspace ml-auto select-none mb-8">
          <h1 class="text-3xl lg:text-4xl">
            {{ sectionHeading('teamspace', 'HomeComponent.teamspace') }}
          </h1>
          <div
            id="div_teamspace"
            class="flex flex-nowrap justify-start min-h-36 gap-1 overflow-x-auto overflow-y-hidden
              items-stretch text-center"
            role="list"
          >
            <member-component
              v-for="member in teamspace"
              :key="member._id"
              :member="member"
              class="shrink-0"
            />
          </div>
        </div>
      </template>

      <template #tasks>
        <div class="home-section home-section--tasks ml-auto w-full">
          <h1 class="text-3xl lg:text-4xl">
            {{ sectionHeading('tasks', 'HomeComponent.myTasks') }}
          </h1>
          <div class="block justify-between gap-1 mt-4" id="div_taskcontainer">
            <task-component v-for="task in assessments" :key="task._id" :task="task" />
            <TravelBuddyCardComponent
              v-if="showTravelBuddyTasksCard"
            />
          </div>
        </div>
      </template>

      <template #personalGoals>
        <div class="home-section home-section--personal-goals overflow-y-visible w-full mt-8">
          <h1 class="text-3xl lg:text-4xl">
            {{ sectionHeading('personalGoals', 'HomeComponent.myPersonalGoals') }}
          </h1>
          <div
            v-if="personalGoals.length !== 0"
            class="grid grid-cols-1 justify-start items-center lg:mr-12 w-full mt-4"
            id="div_personal_goals"
          >
            <goal-component v-for="goal in personalGoals" :key="goal._id" :goal="goal" />
          </div>
          <div v-if="!personalGoals || personalGoals.length === 0">
            <no-personal-goals-component class="mt-5" />
          </div>
        </div>
      </template>
    </HomeScreenSectionLayout>

    <ProvideFeedbackComponent />
    <NewUserDemoOverlayComponent
      v-if="showDialog"
      :visible="showDialog"
      :data="notification"
      @update:visible="showDialog = $event"
    />
    <LevelUpOverlayNoteComponent v-if="isLevelCompleted" />
  </div>
</template>

<script>
import MemberComponent from '@/components/Home/HomeComponent/MemberComponent.vue';
import TaskComponent from '@/components/Home/HomeComponent/TaskComponent.vue';
import GoalComponent from '@/components/Home/HomeComponent/GoalComponent.vue';
import * as push from '@/helpers/webpush/push';
import WeekgoalComponent from '@/components/Home/HomeComponent/WeekgoalComponent.vue';
import QuickAccessLinksComponent from '@/components/Home/HomeComponent/QuickAccessLinksComponent.vue';
import { formatLocaleDate } from '@/helpers/formatLocaleDate';
import { labelFromLocalizedMap, normalizeFeatureLocale } from '@/utils/featureDisplay';
import NoPersonalGoalsComponent from '@/components/Home/PersonalGoalsComponent/NoPersonalGoalsComponent.vue';
import TravelBuddyCardComponent from '@/components/Home/HomeComponent/TravelBuddyCardComponent.vue';
import ProvideFeedbackComponent from '@/components/global/ProvideFeedbackComponent.vue';
import HomeScreenSectionLayout from '@/components/Home/HomeComponent/HomeScreenSectionLayout.vue';

import useWebSocket from '../../../includes/webSockets.client';
import { getCurrentInstance, onMounted, ref } from 'vue';
/* eslint-disable import/no-cycle */
import store from '@/store';
import NewUserDemoOverlayComponent from '../Shared/FrontendOverlays/NewUserDemoOverlayComponent.vue';
import LevelDashboardSection from './LevelDashboardSection.vue';
import LevelUpOverlayNoteComponent from '../Shared/OverlayNotes/LevelUpOverlayNoteComponent.vue';
import { DEFAULT_HOME_SECTIONS } from '@/constants/homeSectionCatalog';

export default {
  name: 'HomeComponent',
  data() {
    return {
      tasksGraber: '',
      overlayGraber: '',
      permissionsGraber: '',
      userInfoGraber: '',
      devTipGraber: '',
      travelBuddySystemActivated: false,
      showDialog: false,
      notification: {},
    };
  },
  components: {
    HomeScreenSectionLayout,
    TravelBuddyCardComponent,
    WeekgoalComponent,
    TaskComponent,
    MemberComponent,
    QuickAccessLinksComponent,
    GoalComponent,
    NoPersonalGoalsComponent,
    ProvideFeedbackComponent,
    NewUserDemoOverlayComponent,
    LevelDashboardSection,
    LevelUpOverlayNoteComponent,
  },
  setup() {
    const { proxy } = getCurrentInstance();
    const token =
      (store && store.state && store.state.auth && store.state.auth.token) ||
      localStorage.getItem('jwt');
    const { socket, isConnected, error } = useWebSocket(token);
    const notifications = ref([]);

    onMounted(() => {
      if (socket.value) {
        socket.value.on('message', (notification) => {
          console.log('Notification received:', notification);
          notifications.value.push(notification);
        });

        socket.value.on('overlays', (overlay) => {
          console.log('Overlays received:', overlay);
          store.commit('setOverlayNotes', overlay);
        });

        // Listen for level progress updates
        socket.value.on('level_progress_update', (levelProgressUpdate) => {
          // levelProgressUpdate.level is the CURRENT level
          // If completed, user just finished this level and advances to level + 1
          const currentLevel = levelProgressUpdate.level;

          // Update Vuex store with real-time level progress data
          store.commit('setLevelInfo', levelProgressUpdate.goals);
          store.commit('setTotalXP', levelProgressUpdate.totalXP);
          store.commit('setCurrentXP', levelProgressUpdate.currentXP);
          store.commit('setCurrentLevel', currentLevel);
          store.commit('setLevelCompleted', levelProgressUpdate.completed);
          store.commit('setDaysSinceLastProgress', 0);
          if (levelProgressUpdate.completed && levelProgressUpdate.showLevelUpPopup !== false) {
            // Show popup(s): current level-up and any already-completed next levels (levelUpSteps)
            // eslint-disable-next-line prefer-destructuring
            const levelUpSteps = levelProgressUpdate.levelUpSteps;
            if (levelUpSteps && levelUpSteps.length > 0) {
              store.commit('showLevelUpPopup', { levelUpSteps });
            } else {
              const oldLevel = currentLevel;
              const newLevel = currentLevel + 1;
              store.commit('showLevelUpPopup', { oldLevel, newLevel });
            }
          }
          store.dispatch('fetchLevelDashboard');
        });

        socket.value.on('general', async (data) => {
          /**
           * Do not refetch getDevTippOverview here: the orange indicator must
           * remain until the user opens a tip; refetching would overwrite the
           * overview and can clear the indicator.
           */
          console.log('General received:', data);
          await Promise.all([
            store.dispatch('getNotifications'),
            proxy.getShortUsers(),
            proxy.getAssessmentsOverview(),
            proxy.getOverlayNotes(),
          ]).catch((err) => {
            console.error('Error fetching data:', err);
          });
        });
      }

      // Listen for push notifications via BroadcastChannel (from service worker)
      if (typeof BroadcastChannel !== 'undefined') {
        const popupChannel = new BroadcastChannel('popup_channel');
        popupChannel.onmessage = async (event) => {
          if (event.data && event.data.type === 'push_notification_received') {
            // Retry logic: Try fetching notifications multiple times with increasing delays
            // This handles the case where the popup might not be saved to DB yet
            const maxRetries = 5;
            const initialDelay = 1000; // Start with 1 second
            let retryCount = 0;

            const fetchWithRetry = async () => {
              try {
                const delay = initialDelay * (2 ** retryCount); // Exponential backoff

                await new Promise((resolve) => {
                  setTimeout(resolve, delay);
                });
                await store.dispatch('getNotifications');

                // Check if we got any notifications
                const popupNotifications = store.getters.getNotifications;

                // If we have notifications or we've exhausted retries, stop
                if (popupNotifications && popupNotifications.length > 0) {
                  return;
                }

                // If no notifications and we have retries left, try again
                if (retryCount < maxRetries - 1) {
                  retryCount += 1;
                  await fetchWithRetry();
                  return;
                }
              } catch (refreshError) {
                console.error('[Push] Error refreshing notifications:', refreshError);
                // Retry on error if we have retries left
                if (retryCount < maxRetries - 1) {
                  retryCount += 1;
                  await fetchWithRetry();
                }
              }
            };

            fetchWithRetry();
          }
        };
      }
    });

    return {
      socket,
      isConnected,
      error,
      notifications,
    };
  },
  computed: {
    teamspace() {
      return this.$store.state.content.teamSpace;
    },
    assessments() {
      return this.$store.state.content.assessments;
    },
    weekGoals() {
      return this.$store.getters.getLevelInfo;
    },
    links() {
      return this.$store.getters.getQuickAccessLinks;
    },
    personalGoals() {
      const personalGoalsArray = this.$store.getters.getPersonalGoals;
      if (!personalGoalsArray || personalGoalsArray.length === 0) {
        return false;
      }
      return personalGoalsArray.filter((goal) => goal.status === 'active');
    },
    isLevelCompleted() {
      return this.$store.getters.getLevelUpPopupShown;
    },
    hasOpenTravelBuddyLevelGoal() {
      return this.weekGoals.some((e) => e.type === 'travelBuddy' && !e.completed);
    },
    hasTravelBuddyLevelGoal() {
      return this.weekGoals.some((e) => e.type === 'travelBuddy');
    },
    /**
     * My Tasks TB card:
     * - Never while a TB level goal is still OPEN (Level Goals only for that meeting).
     * - While a TB level goal is Done: show for a fresh weekly meeting (`created`),
     *   or for `completed` when own feedback is still missing (FA-04.07 — partner
     *   closed the meeting first). Hide for `skipped` and for `completed` with
     *   own feedback already submitted (no My Tasks duplicate).
     * - If the current level has no TB goal at all: show whenever TB system is on.
     */
    showTravelBuddyTasksCard() {
      if (this.travelBuddySystemActivated !== true) {
        return false;
      }
      if (this.hasOpenTravelBuddyLevelGoal) {
        return false;
      }
      if (!this.hasTravelBuddyLevelGoal) {
        return true;
      }
      const meetingInfo = this.$store.getters.getTravelBuddyMeetingInfo;
      if (!meetingInfo?.meetingThisWeek) {
        return false;
      }
      const status = this.$store.getters.getTravelBuddyMeetingStatus;
      if (status === 'created') {
        return true;
      }
      if (status === 'completed') {
        const ownFeedback = this.$store.getters.getTravelBuddyFeedbackText;
        return !String(ownFeedback || '').trim();
      }
      return false;
    },
    isInactive() {
      return this.$store.getters.isInactive;
    },
    daysSinceLastProgress() {
      return this.$store.state.levelManagement?.daysSinceLastProgress ?? 0;
    },
    homeOverviewUpdatedDate() {
      return formatLocaleDate(new Date());
    },
    homeLayoutSections() {
      const layout = this.$store.getters.getHomeScreenLayout;
      if (Array.isArray(layout) && layout.length) {
        return layout;
      }
      return DEFAULT_HOME_SECTIONS;
    },
    orderedEnabledHomeSections() {
      return [...this.homeLayoutSections]
        .filter((section) => section.enabled !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    },
  },
  methods: {
    sectionHeading(sectionKey, i18nFallbackKey) {
      const layout = this.homeLayoutSections.find((s) => s.sectionKey === sectionKey);
      if (layout?.heading) {
        const label = labelFromLocalizedMap(
          layout.heading,
          normalizeFeatureLocale(this.$i18n?.locale),
        );
        if (label) return label;
      }
      return this.$t(i18nFallbackKey);
    },
    async getShortUsers() {
      await this.$store.dispatch('getShortUsers');
    },
    async getAssessmentsOverview() {
      await this.$store.dispatch('getAssessmentsOverview');
    },
    async getOverlayNotes() {
      await this.$store.dispatch('getOverlayNotes');
    },
    async getQuickAccessLinks() {
      await this.$store.dispatch('getQuickAccessLinks');
    },
    async getPersonalGoals() {
      await this.$store.dispatch('getPersonalGoals');
    },
    async getTravelBuddy() {
      await this.$store.dispatch('getTravelBuddyInfo');
    },
    async getWorkshopTags() {
      await this.$store.dispatch('getWorkshopTags');
    },
    async getNewUserDemoContent() {
      await this.$store.dispatch('getNewUserDemoContent');
    },
    async getLevelInfo() {
      await this.$store.dispatch('getCurrentLevelInfo');
    },
  },
  async mounted() {
    try {
      // Await level surfaces together so Home never paints XP counter from
      // getCurrentLevelInfo while weekly XP is still the dashboard default (0).
      await Promise.all([
        this.getLevelInfo(),
        this.$store.dispatch('fetchLevelDashboard'),
        this.$store.dispatch('fetchLevelProgressOverview'),
        this.$store.dispatch('fetchInactivityState'),
      ]);
      this.getShortUsers();
      this.getAssessmentsOverview();
      this.getOverlayNotes();
      this.getQuickAccessLinks();
      this.getPersonalGoals();
      this.getTravelBuddy();
      this.getWorkshopTags();
      this.getNewUserDemoContent();
      this.$store.dispatch('getDevTippOverview');
      await this.$store.dispatch('getUserInfo');
      this.$i18n.locale = this.$store.getters.getUser.preferredLanguage;
      this.travelBuddySystemActivated = this.$store.getters.getUser.travelBuddySystem?.activated;
    } catch (error) {
      console.log('Error fetching data:', error);
    }

    if (import.meta.env.MODE !== 'test') {
      if (await push.checkServiceAvailable()) {
        await push.checkSubscription();
      }
    }
  },
  unmounted() {
    this.$store.commit('removeTeamspace');
    this.$store.commit('removeAssessments');
    clearInterval(this.tasksGraber);
    clearInterval(this.permissionsGraber);
    clearInterval(this.overlayGraber);
    clearInterval(this.userInfoGraber);
    clearInterval(this.devTipGraber);
  },
};
</script>
